import { Track, SongSuggestion } from '../types/playlist';
import { logger } from '../utils/logger';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';

export class AIService {
  private apiUrl: string;

  constructor() {
    // In production (Vercel), use Vercel API proxy
    // In dev, use local backend directly
    this.apiUrl = import.meta.env.DEV
      ? (import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001')
      : '';  // Empty string means use relative URLs (Vercel API routes)
  }

  /**
   * Get AI-powered song suggestions based on user prompt
   */
  async suggestSongs(prompt: string): Promise<SongSuggestion[]> {
    try {
      const count = this.determinePlaylistSize(prompt);
      logger.debug('Requesting AI suggestions:', { prompt, count });

      const response = await fetchWithTimeout(`${this.apiUrl}/api/ai-suggest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          count
        })
      }, 15000); // 15 second timeout for AI

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`AI suggestion failed: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      logger.debug('AI suggested:', data.count, 'songs');

      return data.suggestions;
    } catch (error) {
      logger.error('AI suggestion error:', error);
      throw error;
    }
  }

  /**
   * Filter tracks to remove playlists, compilations, etc.
   */
  async filterRealSongs(tracks: Track[]): Promise<Track[]> {
    try {
      logger.debug('Filtering', tracks.length, 'tracks');

      const response = await fetchWithTimeout(`${this.apiUrl}/api/ai-filter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tracks: tracks.map(t => ({ title: t.title, artist: t.artist }))
        })
      }, 15000); // 15 second timeout for AI

      if (!response.ok) {
        throw new Error('AI filtering failed');
      }

      const data = await response.json();
      logger.debug(`Filtered: ${data.originalCount} → ${data.filteredCount} tracks`);

      // Map valid tracks back to original Track objects
      const validTitles = new Set(data.validTracks.map((t: any) => t.title));
      return tracks.filter(track => validTitles.has(track.title));

    } catch (error) {
      logger.error('AI filtering error:', error);
      // On error, return all tracks (better than nothing)
      return tracks;
    }
  }

  /**
   * Determine playlist size based on query specificity
   * Currently returns fixed size for consistent results
   */
  determinePlaylistSize(prompt: string): number {
    return 30;
  }
}
